import parse from 'html-react-parser';
import {Button, Image} from "antd";
import Link from "next/link";
import React, {useEffect} from "react";
import {CustomPage} from "@/base/types/CustomPage";
import {MDXRemote, MDXRemoteSerializeResult} from "next-mdx-remote";
import * as mdx from "@mdx-js/react";
import vs from 'react-syntax-highlighter/dist/cjs/styles/hljs/vs';
import vs2015 from 'react-syntax-highlighter/dist/cjs/styles/hljs/vs2015';
import {useTheme} from "@/base/components/theme/BaseThemeProvider";
import {getImageUrl} from "@/base/utils/page/page.utils";
import dynamic from "next/dynamic";
import EmbedImage from "@/base/components/page/EmbedImage";
import {default as NextImage} from "next/image";
import {CustomPageSimple} from "@/base/types/CustomPageSimple";
import LinkOutlined from "@ant-design/icons/LinkOutlined";
import CopyToClipboard from "@/base/components/CopyToClipboard";
import reactToText from "react-to-text";

export type Heading = {
	level: number,
	text: string,
	url: string,
	ref?: React.RefObject<HTMLHeadingElement>
};

// import plugins if you need

const DynamicSyntaxHighlighter = dynamic(() => import('react-syntax-highlighter'), {
	ssr: false
});

export const code = ({className, ...properties}: any) => {
	const {theme} = useTheme();

	const [loaded, setLoaded] = React.useState(false);

	useEffect(() => {
		setLoaded(true);
	}, []);

	const match = /language-(\w+)/.exec(className || '')
	return match
		? <div className={`rounded-xl bg-primary-50 dark:bg-base-bg px-2 py-0.5 overflow-auto max-w-[700px]`}>
			{loaded ? <DynamicSyntaxHighlighter language={match[1]} style={theme == 'light' ? vs : vs2015} {...properties} customStyle={{
				background: 'transparent',
				margin: 0,
			}}/> : <pre>
				{properties.children}
			</pre>}
		</div>
		: <code className={className} {...properties} />
}

export const processHtml = (str: string, page?: CustomPageSimple): any => {
	return parse(str, {
		replace(domNode: {
			name: any,
			attribs: any,
			children: any
		} & any) {
			// if img
			if (((domNode.name === 'div' || domNode.name === 'a') && domNode.attribs.class === 'sp-media-preview'))
			{
				const styleAttr = (domNode.children.length > 0 && domNode.children[0].name === 'img') ? domNode.children[0].attribs.style : domNode.attribs.style;

				// style convert to object
				// @ts-ignore
				const styleObj = styleAttr.split(';').map((s: string) => s.trim().split(':')).reduce((prev: any, curr: any) => ({...prev, [curr[0]]: curr[1]}), {});

				const imgUrl = (domNode.children.length > 0 && domNode.children[0].name === 'img') ? domNode.children[0].attribs.src : domNode.attribs.src;
				return <a className={`sp-media-preview`}
					// @ts-ignore
					      src={imgUrl}
					      style={styleObj}>
					<Image
						src={imgUrl}
						rootClassName={`max-w-[500px] rounded-xl overflow-hidden`}
						preview={true}
					/>
				</a>
			}
			// converrt all with class btn
			if (domNode.name === 'a' && domNode.attribs.class && domNode.attribs.class.includes('btn') && domNode.children.length > 0)
			{
				return <Link
					href={domNode.attribs.href}
				>
					<Button
						type="primary"
						// shape="round"
						// size="large"
					>
						{domNode.children[0].data}
					</Button>
				</Link>
			}

			// convert all a to Link who has href starts from / to Link
			if (domNode.name === 'a' && domNode.attribs.href && domNode.attribs.href.startsWith('/'))
			{
				return <Link
					href={domNode.attribs.href}
				>
					{domNode.children.length > 0 ? domNode.children[0].data : domNode.attribs.href}
				</Link>
			}
		},
	});
}

export const HeadingCopyUrl = ({heading}: {
	heading: Heading
}) => {
	return <CopyToClipboard
		copyText={() => {
			const url = new URL(window.location.href);
			url.hash = heading.url;
			return url.toString();
		}}
		onClick={(e) => {
			e.stopPropagation();
		}}
		rootClassName={`opacity-30 hover:opacity-100 hover:text-primary hover:cursor-pointer duration-150 inline`}
	>
		<Link className={` pointer-events-none`}
		      href={`#${heading.url}`}
		      shallow={true}
		>
			<LinkOutlined/>
		</Link>
	</CopyToClipboard>
}

export const getHeadingUrl = (text: string) => {
	console.debug(text, typeof text)
	return `${encodeURIComponent(text.replace(/ /g, '-'))}`;
}

export const processMD = (content?: MDXRemoteSerializeResult<Record<string, unknown>, Record<string, unknown>>, page?: CustomPageSimple, headingsFn?: ((headings: Heading[]) => Promise<void>)) => {
	if (!content)
	{
		return content;
	}
	console.debug(content, "DXDX")

	const headings: Heading[] = [];

	const components: React.ComponentProps<typeof mdx.MDXProvider>['components'] = {
		h1: (props) => {
			const text = reactToText(props.children) || headings.length.toString();
			const ref = React.createRef<HTMLHeadingElement>();
			const heading = {level: 1, text, url: getHeadingUrl(text), ref};
			headings.push(heading);
			return <h1 className={`text-text-2 !font-medium mb-2`} ref={ref}>{props.children} <HeadingCopyUrl heading={heading}/></h1>
		},
		h2: (props) => {
			const text = reactToText(props.children) || headings.length.toString();
			const ref = React.createRef<HTMLHeadingElement>();
			const heading = {level: 2, text, url: getHeadingUrl(text), ref};
			headings.push(heading);
			return <h2 className={`text-text-2 text-3xl !font-medium mb-3 mt-6`} ref={ref}>{props.children} <HeadingCopyUrl heading={heading}/></h2>
		},
		h3: (props) => {
			const text = reactToText(props.children) || headings.length.toString();
			const ref = React.createRef<HTMLHeadingElement>();
			const heading = {level: 3, text, url: getHeadingUrl(text), ref};
			headings.push(heading);
			return <h3 className={`text-text-2 text-xl !font-medium mb-2 mt-4`} ref={ref}>{props.children} <HeadingCopyUrl heading={heading}/></h3>
		},
		h4: (props) => {
			const text = reactToText(props.children) || headings.length.toString();
			const ref = React.createRef<HTMLHeadingElement>();
			const heading = {level: 4, text, url: getHeadingUrl(text), ref};
			headings.push(heading);
			return <h4 className={`text-text-2 text-lg !font-medium mb-2`} ref={ref}>{props.children} <HeadingCopyUrl heading={heading}/></h4>
		},
		h5: (props) => {
			const text = reactToText(props.children) || headings.length.toString();
			const ref = React.createRef<HTMLHeadingElement>();
			const heading = {level: 5, text, url: getHeadingUrl(text), ref};
			headings.push(heading);
			return <h5 className={`text-text-2 !font-medium mb-2`} ref={ref}>{props.children} <HeadingCopyUrl heading={heading}/></h5>
		},
		h6: (props) => {
			const text = reactToText(props.children) || headings.length.toString();
			const ref = React.createRef<HTMLHeadingElement>();
			const heading = {level: 6, text, url: getHeadingUrl(text), ref};
			headings.push(heading);
			return <h6 className={`text-text-2 !font-medium mb-2`} ref={ref}>{props.children} <HeadingCopyUrl heading={heading}/></h6>
		},
		p: (props) => <p className={`mb-2`}>{props.children}</p>,
		ul: (props) => <ul className={`list-disc list-outside ps-4 mb-2`}>{props.children}</ul>,
		ol: (props) => <ol className={`list-decimal list-outside ps-4 mb-2`}>{props.children}</ol>,
		li: (props) => <li className={`mb-2`}>{props.children}</li>,
		a: (props) => <Link href={props.href as string}>{props.children}</Link>,
		code,
		img: (props) => {
			const size = ((props.alt || '').split('#') || []).slice(-2);
			let alt = (props.alt || '').split('#', 2)[0];

			// if alt is empty
			if (!alt && page?.title)
			{
				alt = page.title;
			}

			let width = '500px';
			let height = null;

			let url = getImageUrl(props.src);

			if (size.length > 1 && (size[0].includes("px") || size[0].includes("%") || size[0].includes("vw")))
			{
				width = size[0];
				height = size[1];
			}

			const [loaded, setLoaded] = React.useState(false);
			useEffect(() => {
				setLoaded(true);
			}, []);

			return !loaded ?
				<NextImage
					src={url || ''}
					alt={alt}
					width={parseFloat(width) || 300}
					height={parseFloat(height ?? '100px') || 500}
					layout={"responsive"}
					className={`!block`}
					style={{
						maxWidth: width || '500px',
						maxHeight: height || 'auto',
						// width: 'auto'
					}}
				/> : <div className={`d-block`}>
					<EmbedImage
						suppressHydrationWarning
						src={url}
						alt={alt}
						loading={'lazy'}
						rootClassName={`rounded-xl overflow-hidden`}
						style={{
							maxWidth: width || '500px',
							maxHeight: height || 'auto',
							// width: 'auto'
						}}
					/>
				</div>
		}
	};

	useEffect(() => {
		if (headingsFn)
		{
			headingsFn(headings);
		}
	}, [page]);

	return <Image.PreviewGroup>
		<MDXRemote {...content} components={components}/>
	</Image.PreviewGroup>
}

export const RenderPage = (page: CustomPage, headingsFn?: ((headings: Heading[]) => Promise<void>)): any => {
	if (!page)
	{
		return null;
	}

	const formet = page.format || 'html';

	switch (formet)
	{
		case 'html':
			return processHtml(page.content || '', page);
		case 'md':
			return processMD(page.content_md, page, headingsFn);
		default:
			return null;
	}
}